/**
 * Solanaland
 *
 * Author: Venus Lumanglas
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Modal, Row, Spinner } from "react-bootstrap";
import { Button, Table, SvgIcon, Input, Select } from "core/components";
import moment from "moment";
import ProjectMaterialReturn from "./ProjectMaterialReturn";
import { api } from "core/api";
import { cancel_material_return_admin_project_path } from "routes";
import { MaterialReturnStatus } from "core/schema";

const ProjectMaterialReturns = ({ project, loading, material_returns, sources }): JSX.Element => {
  const [materialReturns, setMaterialReturns] = React.useState(material_returns);
  const [materialReturn, setMaterialReturn] = React.useState(null);
  const [showMaterialReturnModal, setShowMaterialReturnModal] = React.useState(false);
  const [showCancelMaterialReturnModal, setShowCancelMaterialReturnModal] = React.useState(false);
  const [sourceFilter, setSourceFilter] = React.useState("");

  const table = React.useRef(null);

  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        id: 1,
        Header: "MR No.",
        accessor: "code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: (props) => {
          const status = I18n.t(`activerecord.attributes.material_return.statuses.${props.value}`);

          return (
            <div
              className={`d-flex justify-content-center badge badge--pill badge--${props.value.replace("_", "-")}`}
              title={status}
              data-bs-toggle="tooltip"
            >
              <p className="text-truncate">{status}</p>
            </div>
          );
        }
      },
      {
        Header: "Date Created",
        accessor: "created_at",
        Cell: (props) => {
          return (
            <div title={moment(props.value).format("MM/DD/YYYY")} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{moment(props.value).format("MM/DD/YYYY")}</p>
            </div>
          );
        }
      },
      {
        Header: "Date Closed",
        accessor: "date_closed",
        Cell: (props) => {
          return (
            <div title={props.value ? moment(props.value).format("MM/DD/YYYY") : "-"} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value ? moment(props.value).format("MM/DD/YYYY") : "-"}</p>
            </div>
          );
        }
      },
      {
        Header: "Creator",
        accessor: "created_by",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        id: "source",
        Header: "Source",
        accessor: "source",
        Cell: (props) => {
          const source = I18n.t(`activerecord.attributes.material_return.sources.${props.value}`);

          return (
            <div title={source} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{source}</p>
            </div>
          );
        }
      },
      {
        Header: "Actions",
        accessor: "",
        Cell: (props) => {
          const isOpen = props.row.original.status == MaterialReturnStatus.Open;

          return (
            <div className="d-flex justify-content-center">
              <a
                className="button button--action button--action--success"
                title="View Material Return"
                data-bs-toggle="tooltip"
                onClick={() => {
                  setMaterialReturn(props.row.original);
                  setShowMaterialReturnModal(!showMaterialReturnModal);
                }}
              >
                <SvgIcon name="view" className="h5" />
              </a>
              {project.policy.cancel_material_return && isOpen && (
                <a
                  className="button button--action button--action-danger"
                  title="Cancel Material Return"
                  data-bs-toggle="tooltip"
                  onClick={() => {
                    setMaterialReturn(props.row.original);
                    setShowCancelMaterialReturnModal(!showCancelMaterialReturnModal);
                  }}
                >
                  <SvgIcon name="times-circle" className="h6" />
                </a>
              )}
            </div>
          );
        }
      }
    ],
    []
  );

  const updateMaterialReturns = (materialReturns, updatedMaterialReturn) => {
    const updatedMaterialReturns = materialReturns.map((materialReturn) => {
      if (materialReturn.id == updatedMaterialReturn.id) {
        return { ...materialReturn, ...updatedMaterialReturn };
      }

      return materialReturn;
    });

    setMaterialReturns(updatedMaterialReturns);
  };

  const cancelMaterialReturn = React.useCallback(async (materialReturns, materialReturnId) => {
    try {
      const response = await api.put(cancel_material_return_admin_project_path(project.id, { material_return_id: materialReturnId }));
      updateMaterialReturns(materialReturns, response.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  React.useEffect(() => {
    setMaterialReturns(material_returns);
  }, [JSON.stringify(material_returns)]);

  const loader = () => {
    return (
      <div className="d-flex justify-content-center align-items-center h-100 pt-lg-8 pb-lg-8">
        <Spinner animation="border" role="status" variant="brand">
          <span className="visually-hidden">Loading Project Material Returns...</span>
        </Spinner>
      </div>
    );
  };

  const noContent = () => {
    return (
      <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
        <Row className="mb-8">
          <Col>
            <SvgIcon name="clipboard-list" className="display-icon text-gray" />
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>No material returns.</h5>
          </Col>
        </Row>
      </Container>
    );
  };

  const dataFilter = () => {
    return (
      <Row className="mb-3">
        <Col lg={6}>
          <Input placeholder="Search for Material Return Number" onChange={(e) => table.current.setFilter(1, e.target.value)} />
        </Col>
        <Col sm="auto">
          <Select
            placeholder="Filter by Source"
            options={sources}
            isSearchable={false}
            getOptionLabel={(option) => I18n.t(`activerecord.attributes.material_return.sources.${option.value}`)}
            getOptionValue={(option) => option.value}
            onChange={(newOption) => {
              if (newOption) {
                setSourceFilter(newOption.value);
                table.current.setFilter("source", newOption.value);
              } else {
                setSourceFilter("");
                table.current.setFilter("source", "");
              }
            }}
            value={sources.find((source) => source.value == sourceFilter) || ""}
          />
        </Col>
      </Row>
    );
  };

  const dataTable = () => {
    return (
      <Row>
        <Col>
          <Table columns={columns} data={materialReturns} ref={table} />
        </Col>
      </Row>
    );
  };

  const content = () => {
    return materialReturns.length > 0 ? (
      <>
        {dataFilter()}
        {dataTable()}
      </>
    ) : (
      noContent()
    );
  };

  return (
    <React.StrictMode>
      {loading ? loader() : content()}
      <Modal dialogClassName="modal-dialog-centered modal-lg" show={showMaterialReturnModal}>
        <Modal.Header>{`Material Return: ${materialReturn?.code}`}</Modal.Header>
        <Modal.Body>
          <ProjectMaterialReturn materialReturn={materialReturn} />
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowMaterialReturnModal(!showMaterialReturnModal);
            }}
            icon="times"
            title="Close"
          />
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showCancelMaterialReturnModal}>
        <Modal.Header>{`Cancel Material Return: ${materialReturn?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to cancel material return: ${materialReturn?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowCancelMaterialReturnModal(!showCancelMaterialReturnModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--danger"
            icon="times-circle"
            title="Yes, cancel material return."
            onClick={() => {
              cancelMaterialReturn(materialReturns, materialReturn?.id).then(() => {
                setShowCancelMaterialReturnModal(!showCancelMaterialReturnModal);
              });
            }}
          />
        </Modal.Footer>
      </Modal>
    </React.StrictMode>
  );
};

export default ProjectMaterialReturns;
